import React, { forwardRef, useState, useRef, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { IonDatetime, IonDatetimeButton, IonModal } from '@ionic/react';
import { TextInputContainer, Label, ErrorMessage } from '../Text';

const StyledIonDatetimeButton = styled(IonDatetimeButton)`
  --padding-start: 0;
  --padding-end: 0;
  --color: ${({ theme }) => theme.text.regular};
  flex: 1;
  width: 100%;

  &::part(native) {
    border: none;
    outline: none;
    box-shadow: none;
    background: transparent;
    padding: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    justify-content: flex-start;
    text-align: left;
    color: ${({ theme }) => theme.text.regular} !important;
  }

  &::part(text) {
    color: ${({ theme }) => theme.text.regular} !important;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    text-align: left !important;
    justify-content: flex-start;
    width: 100%;
  }

  button {
    color: ${({ theme }) => theme.text.regular} !important;
    text-align: left !important;
    justify-content: flex-start;
  }
`;

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null;
};

const StyledIonDatetime = styled(IonDatetime)`
  --background: ${({ theme }) => theme.background.primary};
  --color: ${({ theme }) => theme.text.regular};
  --wheel-highlight-background: ${({ theme }) => theme.background.light};
  --wheel-fade-background-rgb: ${({ theme }) => hexToRgb(theme.background.primary) || '20, 20, 20'};

  color: ${({ theme }) => theme.text.regular};

  &::part(text) {
    color: ${({ theme }) => theme.text.regular};
  }

  &::part(time-header) {
    color: ${({ theme }) => theme.text.title};
  }

  &::part(time-body) {
    color: ${({ theme }) => theme.text.regular};
  }

  &::part(month-year-button) {
    --color: ${({ theme }) => theme.text.regular};
    color: ${({ theme }) => theme.text.regular};
  }

  &::part(wheel-item) {
    --color: ${({ theme }) => theme.text.regular};
    color: ${({ theme }) => theme.text.regular};
  }

  &::part(wheel-item-active) {
    --color: ${({ theme }) => theme.text.title};
    color: ${({ theme }) => theme.text.title};
  }
`;

const DateInput = forwardRef((props, ref) => {
  const {
    label,
    placeholder,
    value,
    icon,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    error,
    tooltip,
    min,
    max,
    name,
    ...rest
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const datetimeId = useMemo(() => `datetime-${Math.random().toString(36).substr(2, 9)}`, []);
  const modalRef = useRef(null);

  const theme = useTheme();

  const formattedValue = value || '';

  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(modalRef.current);
      } else {
        ref.current = modalRef.current;
      }
    }
  }, [ref]);

  const handleDatetimeChange = (e) => {
    const selectedDate = e.detail.value;
    // IonDatetime returns ISO string (e.g., "2024-01-15T00:00:00Z"), convert to YYYY-MM-DD format
    const dateValue = selectedDate ? selectedDate.split('T')[0] : '';
    if (onChange) {
      const syntheticEvent = {
        target: { value: dateValue },
        currentTarget: { value: dateValue },
      };
      onChange(syntheticEvent);
    }
    // Close the modal immediately after changing the date
    if (modalRef.current) {
      modalRef.current.dismiss();
    }
  };

  const handleDatetimeButtonClick = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleModalDismiss = () => {
    setIsFocused(false);
    onBlur?.();
  };

  return (
    <div style={{ width: '100%', padding: '0 1px', boxSizing: 'border-box' }}>
      {label && <Label>{label}</Label>}
      <TextInputContainer $isFocused={isFocused} $hasError={!!error}>
        {icon && icon}
        <StyledIonDatetimeButton
          datetime={datetimeId}
          onClick={handleDatetimeButtonClick}
          style={{
            '--color': theme.text.regular,
          }}
        />
      </TextInputContainer>
      <IonModal
        ref={modalRef}
        keepContentsMounted={true}
        onDidDismiss={handleModalDismiss}
        style={{
          '--background': theme.background.primary,
        }}
      >
        <StyledIonDatetime
          id={datetimeId}
          value={formattedValue}
          min={min}
          max={max}
          presentation="date"
          onIonChange={handleDatetimeChange}
          {...rest}
        />
      </IonModal>
      {!!error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
});

DateInput.displayName = 'DateInput';

export default DateInput;
